<template>
  <el-drawer id="message-notification-drawer" :model-value="visible" :size="width" :with-header="false" append-to-body class="message-drawer" :modal-class="resizing ? 'message-drawer-overlay message-drawer-resizing' : 'message-drawer-overlay'" @update:model-value="!state.away && !state.detailId && (state.open = $event)" @open-auto-focus="$event?.preventDefault()" @close-auto-focus="closeAutoFocus" @opened="drawerOpened">
    <header class="message-drawer-head">
      <div class="message-drawer-heading"><h2>消息通知</h2><button class="message-read-all" type="button" :disabled="!state.unreadCount || markingReadAll" @click="markAllRead"><BiosIcon name="check-check" :size="15" />全部标为已读</button></div>
      <button class="message-close" type="button" aria-label="关闭消息通知" @click="state.open = false"><BiosIcon name="x" :size="20" /></button>
    </header>
    <div ref="tablist" class="message-tabs" role="tablist" aria-label="消息分类">
      <button v-for="(tab, index) in tabs" :id="tabId(tab.value)" :key="tab.value" type="button" role="tab" :aria-selected="state.category === tab.value" :tabindex="state.category === tab.value ? 0 : -1" aria-controls="message-notification-list" @click="state.category = tab.value" @keydown="changeTab($event, index)">{{ tab.label }}</button>
    </div>
    <div class="message-toolbar">
      <span>{{ state.unreadCount ?? '—' }} 条未读 · {{ state.pendingCount ?? '—' }} 条待处理</span>
      <div class="message-filters">
        <button class="message-filter-switch message-unread-filter" type="button" role="switch" :aria-checked="state.unreadOnly" @click="state.unreadOnly = !state.unreadOnly"><span>只看未读</span><span class="message-filter-track" aria-hidden="true"></span></button>
        <button class="message-filter-switch message-pending-switch" type="button" role="switch" :aria-checked="state.pendingOnly" @click="state.pendingOnly = !state.pendingOnly"><span>仅看待处理</span><span class="message-filter-track" aria-hidden="true"></span></button>
      </div>
    </div>
    <p v-if="state.summaryError" class="message-error" role="alert">{{ state.summaryError }} <button type="button" @click="center.summary()">重试计数</button></p>
    <p v-if="state.realtimeStatus === 'fallback'" class="message-sync-status" role="status">实时连接暂不可用，正在定时刷新</p>
    <button v-if="state.newMessages" class="message-new" type="button" @click="center.load()">有新消息或待处理更新，点击查看</button>
    <div id="message-notification-list" ref="list" class="message-list" role="tabpanel" :aria-labelledby="tabId(state.category)" tabindex="0" :aria-busy="state.loading" @scroll="state.scrollTop = $event.target.scrollTop">
      <div v-for="item in state.items" :key="item.id" class="message-row">
        <button v-message-first-rendered="state.realtimeStatus" class="message-item" type="button" :data-message-id="item.id" @click="openListDetail(item.id)">
          <span class="message-icon" :class="item.category === 'WORK' ? 'work' : 'platform'" :data-tone="presentation(item).tone" aria-hidden="true"><BiosIcon :name="presentation(item).icon" :size="22" :stroke-width="1.7" /></span>
          <span class="message-copy"><strong>{{ item.title }}<i v-if="!item.readAt" class="message-unread" aria-label="未读"></i></strong><MessageResearchContext :message="item" /><span class="message-summary">{{ summary(item) }}</span><span class="message-foot"><time>{{ time(item.occurredAt) }}</time><span v-if="item.processingStatus !== 'INFO'" class="message-status" :data-state="item.processingStatus">{{ statusLabel(item) }}</span></span></span>
        </button>
      </div>
      <p v-if="state.loading" class="message-empty" role="status">正在加载消息…</p>
      <div v-if="state.listError" class="message-error" role="alert">{{ state.listError }} <button type="button" @click="center.retryList()">重试</button></div>
      <div v-else-if="!state.loading && !state.items.length && !state.hasMore" class="message-empty"><BiosIcon name="circle-check" :size="32" :stroke-width="1.4" aria-hidden="true" /><strong>{{ state.unreadOnly ? (state.pendingOnly ? '当前没有未读的待处理消息' : '当前没有未读消息') : (state.pendingOnly ? '当前没有待处理消息' : '暂无消息') }}</strong><p>{{ state.unreadOnly ? '关闭“只看未读”可查看已读消息。' : (state.pendingOnly ? '已完成和仅需查看的消息仍保留在全部消息中。' : '新的通知会在这里显示。') }}</p></div>
      <button v-else-if="state.hasMore" class="message-more" type="button" :disabled="state.loading" @click="center.load(true)">加载更多</button>
      <p v-else-if="!state.loading && !state.listError && state.items.length" class="message-end">没有更多了</p>
    </div>
    <p class="message-drawer-live" role="status" aria-live="polite" aria-atomic="true" data-message-list-status>{{ live }}</p>
    <template #footer><div ref="resizeHandle" class="message-resize" role="separator" :tabindex="fixedWidth ? -1 : 0" aria-label="调整消息抽屉宽度" aria-orientation="vertical" aria-controls="message-notification-drawer" :aria-valuenow="width" :aria-valuemin="limits.min" :aria-valuemax="limits.max" :aria-valuetext="`${width} 像素`" :aria-disabled="fixedWidth" @pointerdown="resize" @lostpointercapture="finishResize" @keydown="resizeByKeyboard"></div></template>
  </el-drawer>
</template>

<script setup>
import { computed, ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { formatBeijingDateTime } from '@/utils/beijingDateTime'
import { messageStatusLabel } from '@/utils/messageStatus'
import { messagePresentation } from '@/utils/messagePresentation'
import { announcementPlainText } from '@/utils/announcementContent'
import BiosIcon from '@/components/BiosIcon.vue'
import MessageResearchContext from '@/components/MessageResearchContext.vue'
const props = defineProps({ center: { type: Object, required: true } })
const state = props.center.state
// Passive render timestamps let acceptance compare actual DOM arrival with producer timing.
// Capture only the first mount: later state refreshes must not rewrite a message's arrival time.
const vMessageFirstRendered = {
  mounted(element, binding) {
    element.dataset.messageFirstRenderedAt = String(Date.now())
    element.dataset.messageRenderedVisibility = document.visibilityState
    element.dataset.messageRenderedChannel = binding.value || 'disabled'
  },
}
const tabs = [{ value: '', label: '全部' }, { value: 'PLATFORM', label: '平台通知' }, { value: 'WORK', label: '工作消息' }]
const tabId = value => `message-tab-${(value || 'all').toLowerCase()}`
const width = ref(480), available = ref(window.innerWidth), list = ref(null), resizeHandle = ref(null), resizing = ref(false)
const tablist = ref(null), live = ref(''), markingReadAll = ref(false)
let entryId = null, focusRowId = null
let detailReturnAnchor = null
const visible = computed(() => state.open && !state.away && !state.detailId)
const limits = computed(() => available.value <= 520
  ? { min: available.value, max: available.value }
  : { min: 320, max: Math.min(available.value, Math.max(480, Math.round(available.value * .8))) })
const fixedWidth = computed(() => limits.value.min === limits.value.max)
const time = value => formatBeijingDateTime(value)
const statusLabel = messageStatusLabel
const presentation = messagePresentation
const summary = item => item.target?.type === 'DATASET_ANNOUNCEMENT' || item.eventType === 'DATASET_ANNOUNCEMENT_PUBLISHED'
  ? announcementPlainText(item.summary) : item.summary
function listAnchors(element) {
  const top = element.getBoundingClientRect().top
  return [...element.querySelectorAll('[data-message-id]')]
    .map(row => ({ id: row.dataset.messageId, offset: row.getBoundingClientRect().top - top, bottom: row.getBoundingClientRect().bottom }))
    .filter(row => row.bottom > top)
}
function restoreAnchor(element, anchors) {
  const rows = new Map([...element.querySelectorAll('[data-message-id]')].map(row => [row.dataset.messageId, row]))
  const anchor = anchors.find(row => rows.has(row.id))
  if (anchor) element.scrollTop += rows.get(anchor.id).getBoundingClientRect().top - element.getBoundingClientRect().top - anchor.offset
}
function openListDetail(id) {
  detailReturnAnchor = list.value && list.value.scrollTop > 8
    ? { category: state.category, pending: state.pendingOnly, unread: state.unreadOnly, anchors: listAnchors(list.value) } : null
  props.center.openDetail(id)
}
// Capture the first row, not a later adjacent article. Manual entry from a business page opens the tabs instead.
watch(() => state.detailId, (id, previous) => {
  if (id && !previous) entryId = id
  if (!id && previous) { focusRowId = state.away ? null : entryId || previous; entryId = null }
}, { flush: 'sync' })
watch(() => state.viewRevision, () => { focusRowId = null; live.value = ''; markingReadAll.value = false }, { flush: 'sync' })
watch(visible, async open => {
  if (!open) { finishResize(); return }
  await nextTick(); fitViewport()
  if (list.value) {
    list.value.scrollTop = state.scrollTop
    if (focusRowId && detailReturnAnchor?.category === state.category && detailReturnAnchor.pending === state.pendingOnly && detailReturnAnchor.unread === state.unreadOnly)
      restoreAnchor(list.value, detailReturnAnchor.anchors)
    state.scrollTop = list.value.scrollTop
    detailReturnAnchor = null
  }
})
watch(() => state.listedThroughId, async () => { await nextTick(); if (list.value && !state.newMessages) list.value.scrollTop = state.scrollTop })
watch(() => state.scrollTop, value => { if (visible.value && list.value) list.value.scrollTop = value }, { flush: 'post' })
// Rows above the reader may disappear after another window completes them.
// Preserve the first surviving visible message, rather than only its old pixel scrollTop.
let anchorRevision = 0
watch(() => state.items, async () => {
  const revision = ++anchorRevision, element = list.value
  if (!visible.value || !element || state.scrollTop <= 8) return
  const view = state.viewRevision, category = state.category, pending = state.pendingOnly, unread = state.unreadOnly
  const anchors = listAnchors(element)
  await nextTick()
  if (revision !== anchorRevision || view !== state.viewRevision || !visible.value || category !== state.category || pending !== state.pendingOnly || unread !== state.unreadOnly) return
  restoreAnchor(element, anchors)
  state.scrollTop = element.scrollTop
})
function selectedTab() { return tablist.value?.querySelector('[aria-selected="true"]') }
function drawerOpened() {
  if (!visible.value) return
  const row = [...(list.value?.querySelectorAll('[data-message-id]') || [])].find(button => button.dataset.messageId === String(focusRowId))
  const target = row || selectedTab()
  target?.focus({ preventScroll: true })
  focusRowId = null
}
function closeAutoFocus(event) {
  // A late leave callback from handing ownership to the detail must not override the new active dialog.
  if (visible.value || state.detailId || state.away) event?.preventDefault()
}
async function markAllRead() {
  if (markingReadAll.value || !state.unreadCount) return
  const view = state.viewRevision
  markingReadAll.value = true; live.value = ''
  try {
    if (await props.center.markAllRead() !== true || view !== state.viewRevision || !visible.value) return
    live.value = '全部消息已标记为已读，待处理状态保持不变'
    await nextTick()
    if (view === state.viewRevision && visible.value) selectedTab()?.focus({ preventScroll: true })
  } finally { if (view === state.viewRevision) markingReadAll.value = false }
}
function changeTab(event, index) {
  if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
  event.preventDefault()
  const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1
    : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length
  state.category = tabs[next].value
  event.currentTarget.parentElement.querySelectorAll('[role="tab"]')[next]?.focus()
}
let preferredWidth = 480, drag = null, cleanup = () => {}
function fit() {
  width.value = Math.round(Math.min(limits.value.max, Math.max(limits.value.min, preferredWidth)))
  return width.value
}
function fitViewport() {
  finishResize()
  available.value = Math.max(0, resizeHandle.value?.closest('.el-overlay')?.getBoundingClientRect().width || window.innerWidth)
  fit()
}
function finishResize() {
  const previous = drag
  drag = null; resizing.value = false
  cleanup(); cleanup = () => {}
  if (previous?.handle.hasPointerCapture?.(previous.id)) previous.handle.releasePointerCapture(previous.id)
}
function resize(event) {
  if (event.button !== 0 || !visible.value || fixedWidth.value || drag) return
  event.preventDefault(); event.stopPropagation()
  drag = { id: event.pointerId, x: event.clientX, width: fit(), handle: event.currentTarget }
  resizing.value = true
  event.currentTarget.focus({ preventScroll: true })
  event.currentTarget.setPointerCapture?.(event.pointerId)
  const move = e => {
    if (!drag || e.pointerId !== drag.id) return
    if (!visible.value) { finishResize(); return }
    e.preventDefault()
    preferredWidth = Math.min(limits.value.max, Math.max(limits.value.min, drag.width + drag.x - e.clientX)); fit()
  }
  const end = e => { if (drag && e.pointerId === drag.id) finishResize() }
  cleanup = () => {
    window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', end)
    window.removeEventListener('pointercancel', end); window.removeEventListener('blur', finishResize)
  }
  window.addEventListener('pointermove', move, { passive: false }); window.addEventListener('pointerup', end)
  window.addEventListener('pointercancel', end); window.addEventListener('blur', finishResize)
}
function resizeByKeyboard(event) {
  if (!visible.value || fixedWidth.value || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
  event.preventDefault(); event.stopPropagation()
  const step = event.shiftKey ? 48 : 16
  preferredWidth = event.key === 'Home' ? limits.value.min : event.key === 'End' ? limits.value.max
    : Math.min(limits.value.max, Math.max(limits.value.min, fit() + (event.key === 'ArrowLeft' ? step : -step)))
  fit()
}
onMounted(() => { fitViewport(); window.addEventListener('resize', fitViewport) })
onBeforeUnmount(() => { finishResize(); window.removeEventListener('resize', fitViewport) })
</script>
